import { TZDate } from "@date-fns/tz";
import { format as d3Format } from "d3-format";
import { intlFormat as dateFnsFormat } from "date-fns";
import { get } from "svelte/store";

import { user } from "$lib/user";
import { type GPUConfig } from "~proto/modal_proto/api";

export function formatSecondsFull(s: number | undefined) {
  if (s === undefined) return "";
  return s.toFixed(3) + "s";
}

export function formatSeconds(s: number | undefined) {
  if (s === undefined) return "—";

  if (s < 1) {
    return (s * 1000).toFixed(0) + "ms";
  } else if (s < 60) {
    return Number.isInteger(s) ? s.toFixed(0) + "s" : s.toFixed(2) + "s";
  }

  // Minutes, hours, days
  const days = Math.floor(s / 86400); // 86400 seconds in a day
  const hours = Math.floor((s % 86400) / 3600);
  const minutes = Math.floor((s % 3600) / 60);
  const seconds = Math.floor(s % 60);

  // Larger days number should use decimal
  if (days > 10) {
    return (s / 86400).toFixed(2) + " days";
  }

  // Only include two units e.g. 2h 30m
  let result = "";
  if (days > 0) {
    result += days + "d " + hours + "h";
  } else if (hours > 0) {
    result += hours + "h " + minutes + "m";
  } else if (minutes > 0) {
    result += minutes + "m " + seconds + "s";
  } else {
    result += seconds + "s";
  }

  return result;
}

export function dateInUserTimezone(date: Date): Date {
  const u = get(user);
  const timezone = (u?.logged_in && u?.timezone) || undefined;
  return new TZDate(date, timezone);
}

export function timestampToString(
  timestamp: number | undefined,
  options: {
    showMilliseconds: boolean;
    showDate: boolean;
    timezone?: string;
  } = {
    showMilliseconds: false,
    showDate: true,
  },
): string {
  const u = get(user);
  const userTimezone =
    options.timezone || (u?.logged_in && u?.timezone) || undefined;
  const dateTimeOptions: Intl.DateTimeFormatOptions = {
    hour: "numeric",
    minute: "numeric",
    second: "numeric",
    // 24 hour time (Int.DateTimeFormat provides an option for h24, but this means counting starts from 1 and goes up to 24.
    // We prefer h23 to count from 0:00:00 to 23:59:59)
    hourCycle: "h23",
    fractionalSecondDigits: options?.showMilliseconds ? 3 : undefined,
    year: options?.showDate ? "numeric" : undefined,
    month: options?.showDate ? "short" : undefined,
    day: options?.showDate ? "numeric" : undefined,
  };

  if (!timestamp) return "—";
  const date = new Date(timestamp * 1000);
  try {
    return dateFnsFormat(
      date,
      {
        ...dateTimeOptions,
        timeZone: userTimezone || undefined,
      },
      {
        locale: "en-US",
      },
    );
  } catch (e) {
    console.error(e);
    return dateFnsFormat(date, dateTimeOptions, {
      locale: "en-US",
    });
  }
}

export function formatDollars(
  dollars: number,
  options?: { showZeroCents?: boolean },
): string {
  if (!options?.showZeroCents && Number.isInteger(dollars)) {
    return "$" + dollars.toLocaleString("en-US");
  } else {
    return (
      "$" +
      dollars.toLocaleString("en-US", {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      })
    );
  }
}

export function formatBytes(
  value: number,
  options?: { decimals?: number; short?: boolean },
): string {
  if (value < 0) {
    return "-" + formatBytes(-value, options);
  }

  const decimals = options?.decimals ?? 1;
  const short = options?.short ?? false;

  let suffixes = [" B", " KiB", " MiB", " GiB", " TiB", " PiB", " EiB", " ZiB"];
  if (short) {
    suffixes = ["", "K", "M", "G", "T", "P", "E", "Z"];
  }
  const base = 1024;
  let i;
  for (i = 0; i < suffixes.length; i++) {
    if (value < Math.pow(base, i + 1)) {
      break;
    }
  }
  if (i === 0) {
    return Math.round(value) + suffixes[i];
  } else {
    return (value / Math.pow(base, i)).toFixed(decimals) + suffixes[i];
  }
}

export function formatNumber(value: number): string {
  return d3Format(".2s")(value);
}

export function formatInteger(value: number): string {
  return Math.abs(value) < 1000
    ? value.toString()
    : new Intl.NumberFormat("en", {
        notation: "compact",
        maximumFractionDigits: 1,
      })
        .format(value)
        .toLowerCase();
}

export function formatGpuType(
  gpuType: string,
  options?: { includeSuffix?: boolean },
): string {
  return gpuType + (options?.includeSuffix ? " GPU" : "");
}

export function formatGpuConfig(
  gpuConfig: GPUConfig,
  options?: { includeSuffix?: boolean },
): string {
  return (
    (gpuConfig.count > 1 ? `${gpuConfig.count} × ` : "") +
    formatGpuType(gpuConfig.gpuType, options)
  );
}

export function formatList(
  items: string[],
  options?: { connector?: "and" | "or" },
): string {
  const connector = options?.connector ?? "and";
  if (items.length === 0) {
    return "";
  } else if (items.length === 1) {
    return items[0];
  } else if (items.length === 2) {
    return items.join(` ${connector} `);
  } else {
    // ["a", "b", "c"] -> "a, b, and c"
    const allButLast = items.slice(0, -1).join(", ");
    const last = items[items.length - 1];
    return `${allButLast}, ${connector} ${last}`;
  }
}

export function pluralize(unit: string, count: number): string {
  return count === 1 ? unit : unit + "s";
}
